import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { AlertTriangle } from "lucide-react";
import { apiError } from "@/lib/api";

const STATUS_STYLES = {
  not_started: "bg-slate-100 text-slate-600 border-slate-200",
  roster_only: "bg-indigo-50 text-indigo-700 border-indigo-200",
  update: "bg-indigo-50 text-indigo-700 border-indigo-200",
  unknown_state: "bg-rose-50 text-rose-700 border-rose-200",
  draft: "bg-amber-50 text-amber-700 border-amber-200",
  submitted: "bg-emerald-50 text-emerald-700 border-emerald-200",
  change_requested: "bg-orange-50 text-orange-700 border-orange-200",
  reopened: "bg-blue-50 text-blue-700 border-blue-200",
  pending: "bg-amber-50 text-amber-700 border-amber-200",
  accepted: "bg-emerald-50 text-emerald-700 border-emerald-200",
  revoked: "bg-slate-100 text-slate-500 border-slate-200",
  expired: "bg-rose-50 text-rose-700 border-rose-200",
  sent: "bg-emerald-50 text-emerald-700 border-emerald-200",
  failed: "bg-rose-50 text-rose-700 border-rose-200",
  not_sent: "bg-slate-100 text-slate-600 border-slate-200",
  active: "bg-emerald-50 text-emerald-700 border-emerald-200",
  pending_approval: "bg-amber-50 text-amber-700 border-amber-200",
  disabled: "bg-rose-50 text-rose-700 border-rose-200",
  ok: "bg-emerald-50 text-emerald-700 border-emerald-200",
  duplicate_in_file: "bg-orange-50 text-orange-700 border-orange-200",
  already_exists: "bg-amber-50 text-amber-700 border-amber-200",
  invalid_email: "bg-rose-50 text-rose-700 border-rose-200",
  missing_required: "bg-rose-50 text-rose-700 border-rose-200",
  unknown_master: "bg-rose-50 text-rose-700 border-rose-200",
};

export function StatusBadge({ status }) {
  const label = String(status || "").replace(/_/g, " ");
  return (
    <Badge
      variant="outline"
      data-testid={`status-${status}`}
      className={`font-medium capitalize ${STATUS_STYLES[status] || "bg-slate-100 text-slate-600 border-slate-200"}`}
    >
      {label}
    </Badge>
  );
}

export function PageHeader({ title, description, actions }) {
  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between mb-8">
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-slate-900">{title}</h1>
        {description && <p className="mt-1 text-sm text-slate-500">{description}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}

export function EmptyState({ title, hint, icon: Icon }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-slate-200 bg-slate-50/50 py-16 text-center" data-testid="empty-state">
      {Icon && <Icon className="mb-3 h-8 w-8 text-slate-300" />}
      <p className="font-medium text-slate-700">{title}</p>
      {hint && <p className="mt-1 text-sm text-slate-400">{hint}</p>}
    </div>
  );
}

export function Loading({ label = "Loading…" }) {
  return (
    <div className="flex items-center justify-center gap-3 py-16 text-slate-400" data-testid="loading-state">
      <div className="h-5 w-5 animate-spin rounded-full border-2 border-slate-200 border-t-indigo-600" />
      <span className="text-sm">{label}</span>
    </div>
  );
}

export function ErrorState({ error, onRetry }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-rose-200 bg-rose-50/40 py-16 text-center" data-testid="error-state">
      <AlertTriangle className="mb-3 h-8 w-8 text-rose-400" />
      <p className="font-medium text-slate-700">Couldn't load this</p>
      <p className="mt-1 max-w-sm text-sm text-slate-400">{apiError(error?.response?.data?.detail)}</p>
      {onRetry && (
        <Button variant="outline" size="sm" className="mt-4" data-testid="error-retry" onClick={() => onRetry()}>
          Try again
        </Button>
      )}
    </div>
  );
}

export function ListState({ isLoading, isError, error, onRetry, isEmpty, emptyTitle, emptyHint, icon, children }) {
  if (isLoading) return <Loading />;
  if (isError) return <ErrorState error={error} onRetry={onRetry} />;
  if (isEmpty) return <EmptyState title={emptyTitle} hint={emptyHint} icon={icon} />;
  return children;
}
